import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pause, Play } from "lucide-react";
import type { ExplorerSelection } from "@/features/explorer/types";
import type { MapPeriods } from "@/features/explorer/api/contracts";
import { explorerQueryKeys, ANALYTICS_STALE_TIME_MS, ANALYTICS_GC_TIME_MS } from "@/features/explorer/api/query-keys";
import { getMapPeriods } from "@/features/explorer/services/national-data-api";
import { formatPeriodLabel } from "@/features/explorer/lib/format";
import { cn } from "@/lib/utils";

const PLAYBACK_INTERVAL_MS = 1500;

type PeriodTimelineProps = {
  selection: ExplorerSelection;
  onSelectionChange: (selection: ExplorerSelection) => void;
  className?: string;
};

/**
 * Year slider + playback for the single-metric map. Scrub through the periods
 * the API reports for the current metric/statistic. Play animates from the
 * earliest period through to the latest, one period every 1.5s, then stops.
 * The selected period is written back into the explorer selection (and
 * therefore the URL) so the map slice refetches.
 */
export function PeriodTimeline({ selection, onSelectionChange, className }: PeriodTimelineProps) {
  const { data } = useQuery<MapPeriods>({
    queryKey: explorerQueryKeys.mapPeriods({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
    }),
    queryFn: () =>
      getMapPeriods({
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
      }),
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  const periods = useMemo(() => data?.periods ?? [], [data]);

  const activeIndex = useMemo(() => {
    if (periods.length === 0) return 0;
    const idx = periods.indexOf(selection.period);
    if (idx !== -1) return idx;
    return periods.length - 1;
  }, [periods, selection.period]);

  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Latest-value refs so the playback interval keeps ticking through
  // re-renders without being torn down every time the selection changes.
  const selectionRef = useRef(selection);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const periodsRef = useRef(periods);
  const activeIndexRef = useRef(activeIndex);

  useEffect(() => {
    selectionRef.current = selection;
    onSelectionChangeRef.current = onSelectionChange;
    periodsRef.current = periods;
    activeIndexRef.current = activeIndex;
  });

  // Stable identity (refs + setState only) so the playback interval below can
  // depend on it without being torn down on every render.
  const stopPlayback = useCallback(() => {
    if (timerRef.current !== null) {
      globalThis.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    if (!isPlaying || periodsRef.current.length === 0) return () => {};
    const list = periodsRef.current;
    timerRef.current = globalThis.setInterval(() => {
      const currentIdx = activeIndexRef.current;
      if (currentIdx >= list.length - 1) {
        stopPlayback();
        return;
      }
      const nextIndex = currentIdx + 1;
      activeIndexRef.current = nextIndex;
      const nextPeriod = list[nextIndex];
      if (nextPeriod) onSelectionChangeRef.current({ ...selectionRef.current, period: nextPeriod });
    }, PLAYBACK_INTERVAL_MS);
    return () => {
      if (timerRef.current !== null) {
        globalThis.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, stopPlayback]);

  const togglePlayback = () => {
    if (isPlaying) {
      stopPlayback();
      return;
    }
    // Start the animation from the earliest period.
    const list = periodsRef.current;
    if (list.length > 0) {
      activeIndexRef.current = 0;
      if (selectionRef.current.period !== list[0]) {
        onSelectionChangeRef.current({ ...selectionRef.current, period: list[0] });
      }
    }
    setIsPlaying(true);
  };

  if (periods.length < 2) return null;

  const currentLabel = formatPeriodLabel(periods[activeIndex]);
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < periods.length - 1;

  const goTo = (index: number) => {
    const period = periods[index];
    if (period) onSelectionChange({ ...selection, period });
  };

  return (
    <div className={cn("border-t border-border/80 pt-2.5 font-sans", className)}>
      <div className='mb-1.5 flex items-center justify-between gap-2'>
        <span className='text-xs font-semibold tracking-wider text-muted-foreground uppercase'>Time period</span>
        <button
          type='button'
          onClick={togglePlayback}
          className={cn(
            "flex cursor-pointer items-center gap-1 rounded-xs border px-1.5 py-0.5 text-xs font-semibold transition-colors",
            isPlaying
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-primary",
          )}
          title={isPlaying ? "Pause playback" : "Play through all periods"}>
          {isPlaying ? <Pause className='size-3' /> : <Play className='size-3' />}
          {isPlaying ? "Pause" : "Play"}
        </button>
      </div>

      <div className='flex items-center gap-2'>
        <span className='w-18 shrink-0 font-mono text-sm font-bold text-card-foreground'>{currentLabel}</span>
        <input
          type='range'
          min={0}
          max={periods.length - 1}
          step={1}
          value={activeIndex}
          onChange={(event) => {
            goTo(Number(event.target.value));
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft" && hasPrev) goTo(activeIndex - 1);
            if (event.key === "ArrowRight" && hasNext) goTo(activeIndex + 1);
          }}
          className='h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted'
          aria-label='Time period slider'
        />
      </div>

      <p className='mt-1 text-xs text-muted-foreground'>
        {formatPeriodLabel(periods[0])} – {formatPeriodLabel(periods.at(-1) ?? periods[0])} · {periods.length}{" "}
        {periods.length === 1 ? "period" : "periods"}
      </p>
    </div>
  );
}
